import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(localizedFormat);

/**
 * Get query object from URL
 *
 * @param {string} url
 */
export function getQueryObject(url: string) {
  url = url == null ? window.location.href : url;
  const search = url.substring(url.lastIndexOf('?') + 1);
  const obj = {};
  const reg = /([^?&=]+)=([^?&=]*)/g;
  search.replace(reg, (rs, $1, $2) => {
    const name = decodeURIComponent($1);
    let val = decodeURIComponent($2);
    val = String(val);
    // @ts-ignore
    obj[name] = val;
    return rs;
  });

  return obj;
}

/**
 * @param {string} input value
 * @returns {number} output value
 */
export function byteLength(str: string) {
  // returns the byte length of an utf8 string
  let s = str.length;
  for (var i = str.length - 1; i >= 0; i--) {
    const code = str.charCodeAt(i);
    if (code > 0x7f && code <= 0x7ff) {
      s++;
    } else if (code > 0x7ff && code <= 0xffff) {
      s += 2;
    }
    if (code >= 0xdc00 && code <= 0xdfff) {
      i--;
    }
  }
  return s;
}

/**
 * Remove invalid (not equal true) elements from array
 *
 * @param {Array} actual
 */
export function cleanArray(actual: any) {
  const newArray = [];
  for (let i = 0; i < actual.length; i++) {
    if (actual[i]) {
      newArray.push(actual[i]);
    }
  }

  return newArray;
}

/**
 * Parse params from URL and return an object
 *
 * @param {string} url
 */
export function param2Obj(url: string) {
  const search = url.split('?')[1];
  if (!search) {
    return {};
  }
  return JSON.parse(
    '{"' +
      decodeURIComponent(search)
        .replace(/"/g, '\\"')
        .replace(/&/g, '","')
        .replace(/=/g, '":"')
        .replace(/\+/g, ' ') +
      '"}'
  );
}

/**
 * @param {string} val
 */
export function html2Text(val: string) {
  const div = document.createElement('div');
  div.innerHTML = val;

  return div.textContent || div.innerText;
}

/**
 * Merges two  objects, giving the last one precedence
 *
 * @param {Object} target
 * @param {Object} source
 */
export function objectMerge(target: any, source: any) {
  if (typeof target !== 'object') {
    target = {};
  }
  if (Array.isArray(source)) {
    return source.slice();
  }
  Object.keys(source).forEach((property) => {
    const sourceProperty = source[property];
    if (typeof sourceProperty === 'object') {
      target[property] = objectMerge(target[property], sourceProperty);
    } else {
      target[property] = sourceProperty;
    }
  });

  return target;
}

/**
 * @param {HTMLElement} element
 * @param {string} className
 */
export function toggleClass(element: HTMLElement, className: string) {
  if (!element || !className) {
    return;
  }
  let classString = element.className;
  const nameIndex = classString.indexOf(className);
  if (nameIndex === -1) {
    classString += '' + className;
  } else {
    classString =
      classString.substr(0, nameIndex) +
      classString.substr(nameIndex + className.length);
  }

  element.className = classString;
}

export function getTime(type: string) {
  if (type === 'start') {
    return new Date().getTime() - 3600 * 1000 * 24 * 90;
  } else {
    return new Date(new Date().toDateString());
  }
}

/**
 * @param {Object[]} arr
 * @returns {Object[]}
 */
export function uniqueArr(arr: any) {
  return Array.from(new Set(arr));
}

/**
 * Check if an element has a class
 *
 * @param {HTMLElement} elm
 * @param {String} cls
 */
export function hasClass(ele: HTMLElement, cls: string) {
  return !!ele.className.match(new RegExp('(\\s|^)' + cls + '(\\s|$)'));
}

/**
 * Add class to element
 *
 * @param {HTMLElement} elm
 * @param {String} cls
 */
export function addClass(ele: HTMLElement, cls: string) {
  if (!hasClass(ele, cls)) {
    ele.className += ' ' + cls;
  }
}

/**
 * Remove class from element
 *
 * @param {HTMLElement} elm
 * @param {String} cls
 */
export function removeClass(ele: HTMLElement, cls: string) {
  if (hasClass(ele, cls)) {
    const reg = new RegExp('(\\s|^)' + cls + '(\\s|$)');
    ele.className = ele.className.replace(reg, ' ');
  }
}

/**
 * Trunacte string and add ellipsis
 * @param str
 * @returns
 */
export function truncateString(str: string, count: number = 20) {
  if (str) return str.slice(0, count) + (str.length > count ? '...' : '');
}

export function capitalizeFirstLetter(str: string) {
  if (str) return str && str[0].toUpperCase() + str.slice(1);
}

export const cleanData = (formData: any) => {
  let cleanObj = {};
  Object.keys(formData).forEach((val: any) => {
    const newVal = formData[val];
    cleanObj = newVal ? { ...cleanObj, [val]: newVal } : cleanObj;
  });
  return cleanObj;
};

/**
 * Unmask phone number before calling API endpoint
 * Input -> Output: (+49 123 123456) -> 49123123456
 *
 * @param {string} value
 * @returns {string}
 */
export const unmaskPhone = (value: string) => {
  if (!value) return value;
  return value.replace(/[^\d]/g, '');
};

/**
 * Mask phone number to display in proper format
 * Input -> Output: 49123123456 -> (+49 123 123456)
 *
 * @param {string} phone
 * @returns {string}
 */
export const maskPhone = (phone: string) => {
  if (!phone) return phone;
  const x: any = phone.replace(/\D/g, '').match(/(\d{0,2})(\d{0,3})(\d{0,12})/);
  phone = !x[2] ? x[1] : '(+' + x[1] + ') ' + x[2] + (x[3] ? ' ' + x[3] : '');
  return phone ? phone : '';
};

export function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 *
 * @param {string} inputValue
 * @returns {string} resultValue(formated time)
 */
export const maskSpentTime = (inputValue: string): string => {
  const value = inputValue && inputValue?.split(':').join('');

  const hour = value && value?.substr(0, 2);
  const minute = value && value?.substr(2, 2);
  const second = value && value?.substr(4, 2);

  let resultValue: string = '';

  if (hour) {
    resultValue = hour;
  }
  if (minute) {
    resultValue += ':' + minute;
  }
  if (second) {
    resultValue += ':' + second;
  }
  return resultValue;
};

/**
 * Generate a unique id
 * Helpful for identifying elements of an array when id is not sent by server
 *
 * @returns a unique id (For eg: _a3hxejbt1)
 */
export const generateUniqueId = () => {
  return '_' + Math.random().toString(36).substr(2, 9);
};

/**
 * Get next letter in the English Alphabet
 * Input a -> Output b, Input A -> Output B
 *
 * @param s
 * @returns
 */
export const getNextLetter = (s: string) => {
  return s.replace(/([a-zA-Z])[^a-zA-Z]*$/, function (a: string) {
    let c = a.charCodeAt(0);
    switch (c) {
      case 90:
        return 'A';
      case 122:
        return 'a';
      default:
        return String.fromCharCode(++c);
    }
  });
};

/**
 * Get starting serial number (S.N.) based on current page and page size
 * The starting S.N. is dynamic with page
 *
 * page = 1, limit = 5 ->  staring SN = 1
 * page = 2, limit = 5 ->  staring SN = 6
 *
 * @param currentPage
 * @param pageSize
 * @returns
 */
export const getStartingSerialNumber = (
  currentPage: number,
  pageSize: number
) => {
  return currentPage * pageSize - (pageSize - 1);
};

export const buildFormData = (
  model: any,
  form?: FormData,
  namespace = ''
): FormData => {
  let formData = form || new FormData();
  let formKey;

  for (let propertyName in model) {
    if (!model.hasOwnProperty(propertyName) || !model[propertyName]) continue;
    let formKey = namespace ? `${namespace}[${propertyName}]` : propertyName;
    if (model[propertyName] instanceof Date)
      formData.append(formKey, model[propertyName].toISOString());
    else if (model[propertyName] instanceof File)
      formData.append(formKey, model[propertyName]);
    else if (model[propertyName] instanceof Array) {
      model[propertyName].forEach((element: any, index: any) => {
        const tempFormKey = `${formKey}[${index}]`;
        buildFormData(element, formData, tempFormKey);
      });
    } else if (
      typeof model[propertyName] === 'object' &&
      !(model[propertyName] instanceof File)
    )
      buildFormData(model[propertyName], formData, formKey);
    else formData.append(formKey, model[propertyName].toString());
  }
  return formData;
};

export const formatPrice = (value: number) => {
  let euroCurrency;
  euroCurrency = (value / 100).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
  });
  return euroCurrency;
};

export const convertToFormData = (fData: any) => {
  const data = new FormData();

  for (const key in fData) {
    if (key === 'field') {
      data.append(key, fData[key][1]);
    } else {
      data.append(key, fData[key]);
    }
  }
  return data;
};

export const DateFormatYMD = (date: any): string => {
  if (!date) return date;
  return dayjs(date).format('YYYY-MM-DD');
};

export const DateFormatMDY = (date: any): string => {
  if (!date) return date;
  return dayjs(date).format('MM/DD/YYYY');
};

export const DateFormat = (date: any): string => {
  if (!date) return date;
  return dayjs(date).format('MM/DD/YYYY');
};

export const DateFormatTime = (dateTime: any): string => {
  if (!dateTime) return dateTime;
  return dayjs(dateTime).format('MM/DD/YYYY h:mm:ss A');
};

export const TimeFormat = (dateTime: any): string => {
  if (!dateTime) return dateTime;
  return dayjs(dateTime).format('h:mm:ss A');
};

export const groupArrayOfObjects = (list: any, key: any) => {
  return list.reduce(function (rv: any, x: any) {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
};
